import {
  Fragment,
  useState,
  useCallback,
  memo,
  useEffect,
  useRef,
} from 'react';
import { isAfter } from 'date-fns';
import {
  Avatar,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Tooltip,
  MenuList,
  MenuItem,
  Button,
} from '@mui/material';
import { paths, generateValidatedParamsUrl } from '../../lib';
import { AccessTimeIcon, ErrorIcon } from '../../assets';
import {
  useDispatch,
  useSelector,
  logout,
  toggleSidebar,
  uiSelectors,
  clearSelectedIds,
  systemSelectors,
} from '../../state';
import { useGetUsersData } from '../../hooks';
import { GlobalWarehouseSelectModal } from '../common/modals/GlobalWarehouseSelectModal';
import { /*FrequentlyAccessed, */ Alerts } from '../ui';
import { useStyles } from './Sidebar.styles';
import { SidebarItem } from './SidebarItem';
import { getAssignedServicesBasicList } from '../../state/asyncActions/services';
import {
  AdminSidebar,
  CustomerServiceSidebar,
  TruckingJobUserSidebar,
  CustomerSidebar,
  ProcessorSidebar,
  ScannerSidebar,
  ScannerProcessorSidebar,
  AppointmentUserSidebar,
  ManagerUserSidebar,
} from './userSidebars';
import TplLogo from '../../assets/images/3pl-logo.svg';
import TplLogoSmall from '../../assets/images/3pl-logo-small.svg';

export const Sidebar = memo(function Sidebar() {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const isSidebarOpen = useSelector(uiSelectors.isSidebarOpen);
  const hasMultipleWarehouses = useSelector(
    (state) => state.authPersist.data.hasMultipleWarehouses,
  );

  const {
    firstName,
    lastName,
    isAdmin,
    isCustomer,
    isCustomerService,
    isTruckingSalesPerson,
    isProcessor,
    isScanner,
    isScannerProcessor,
    isManageAppointmentUser,
    isManager,
    token,
    expiration,
  } = useGetUsersData();

  const hasValidToken = !!token && !isAfter(new Date(), new Date(expiration));
  const [anchorEl, setAnchorEl] = useState(null);
  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);

  useEffect(() => {
    if (hasValidToken && !isCustomer && !isManageAppointmentUser) {
      dispatch(getAssignedServicesBasicList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer, hasValidToken]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback((event) => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(
    (e) => {
      handleClose(e);
      dispatch(logout());
    },
    [dispatch, handleClose],
  );

  const handleToggleSidebar = useCallback(() => {
    dispatch(toggleSidebar(!isSidebarOpen));
  }, [dispatch, isSidebarOpen]);

  const handleClearSelectedOrders = useCallback(() => {
    dispatch(clearSelectedIds({ resource: 'orders' }));
  }, [dispatch]);

  return (
    <Fragment>
      <Drawer
        variant='permanent'
        className={cx({
          [classes.drawerOpen]: isSidebarOpen,
          [classes.drawerClose]: !isSidebarOpen,
        })}
        classes={{
          paper: cx({
            [classes.drawerOpen]: isSidebarOpen,
            [classes.drawerClose]: !isSidebarOpen,
          }),
        }}
      >
        <div className={classes.content}>
          <div>
            <List>
              <ListItemButton
                onClick={handleToggleSidebar}
                // className={classes.ListItem}
              >
                <div className={classes.logo}>
                  {isSidebarOpen ? (
                    <TplLogo draggable={false} />
                  ) : (
                    <TplLogoSmall draggable={false} />
                  )}
                </div>
                {/* <ListItemIcon>
                  <TplLogo />
                </ListItemIcon> */}
              </ListItemButton>
            </List>
            <List>
              {isAdmin && (
                <AdminSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isCustomerService && (
                <CustomerServiceSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isTruckingSalesPerson && (
                <TruckingJobUserSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isProcessor && !isScannerProcessor && (
                <ProcessorSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isScannerProcessor && (
                <ScannerProcessorSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isScanner && !isScannerProcessor && <ScannerSidebar />}
              {isCustomer && (
                <CustomerSidebar
                  handleClearSelectedOrders={handleClearSelectedOrders}
                />
              )}
              {isManageAppointmentUser && <AppointmentUserSidebar />}
              {isManager && <ManagerUserSidebar />}
            </List>
          </div>
          <div>
            <List>
              {!isCustomer && !isManageAppointmentUser && (
                <SidebarItem
                  to={generateValidatedParamsUrl(paths.TRACK_TIME)}
                  text=''
                  icon={AccessTimeWithWarningIcon}
                />
              )}
              {!isManageAppointmentUser && (
                <ListItemButton style={{ paddingLeft: 14 }}>
                  {/* <FrequentlyAccessed /> */}
                  <Alerts />
                </ListItemButton>
              )}
            </List>
          </div>
        </div>
        <ListItemButton
          onMouseEnter={handleClick}
          onMouseLeave={handleClose}
          disableGutters
          style={{ paddingLeft: 14 }}
        >
          <ListItemIcon>
            <Avatar className={classes.avatar}>{`${
              firstName?.charAt(0)?.toUpperCase() || ''
            }${lastName?.charAt(0)?.toUpperCase() || ''}`}</Avatar>
          </ListItemIcon>
          {isSidebarOpen && (
            <ListItemText primary={`${firstName || ''} ${lastName || ''}`} />
          )}
          <Popper
            className={classes.logout}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement={'right'}
          >
            <MenuList
              autoFocusItem={Boolean(anchorEl)}
              sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
            >
              {hasMultipleWarehouses && (
                <MenuItem
                  onClick={() => {
                    setWarehouseModalOpen(true);
                    handleClose();
                  }}
                >
                  Update Warehouse
                </MenuItem>
              )}
              <MenuItem
                sx={{
                  padding: 0,
                }}
                onClick={handleLogout}
              >
                <Button
                  id='log-out'
                  color='primary'
                  variant='contained'
                  onClick={handleLogout}
                  fullWidth
                >
                  Log out
                </Button>
              </MenuItem>
            </MenuList>
          </Popper>
        </ListItemButton>
      </Drawer>
      {warehouseModalOpen && (
        <GlobalWarehouseSelectModal
          onClose={() => setWarehouseModalOpen(false)}
          open={warehouseModalOpen}
        />
      )}
    </Fragment>
  );
});

const AccessTimeWithWarningIcon = (props) => {
  const assignedServices = useSelector(systemSelectors.assignedServices);
  const iconRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (iconRef.current) {
      setIsMounted(true);
    }
  }, []);

  return (
    <Box position='relative' display='inline-flex'>
      <AccessTimeIcon {...props} />
      {assignedServices?.length > 0 && (
        <Box
          position='absolute'
          top={-8}
          right={-8}
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <Tooltip
            title='You have open tasks waiting to be worked on'
            // from YB: I fixed the warning in the console but I don't understand why it's always open
            open={isMounted}
            placement='right'
          >
            <ErrorIcon ref={iconRef} color='error' fontSize='small' />
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
